import { useState, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { linkifyWhatsApp } from "@/lib/linkifyWhatsApp";
import React from "react";
import { getValidToken } from "@/hooks/api/auth/tokenManager";

const PSI_CHAT_URL = `${process.env.REACT_APP_PSI_PLATFORM_BE_URL ?? "http://localhost:3001"}/api/psi-chat/message`;

type Message = { role: "user" | "assistant"; content: string };

function processChildren(children: React.ReactNode): React.ReactNode {
  if (typeof children === "string") return linkifyWhatsApp(children);
  if (Array.isArray(children))
    return children.map((child, i) => {
      if (typeof child === "string")
        return <React.Fragment key={i}>{linkifyWhatsApp(child)}</React.Fragment>;
      if (React.isValidElement(child)) {
        const props = child.props as Record<string, unknown>;
        return React.cloneElement(child, { ...props, key: i } as React.Attributes, processChildren(props.children as React.ReactNode));
      }
      return child;
    });
  if (React.isValidElement(children)) {
    const props = children.props as Record<string, unknown>;
    return React.cloneElement(children, props as React.Attributes, processChildren(props.children as React.ReactNode));
  }
  return children;
}

const SUGGESTIONS = [
  "¿Cómo hablo de horarios con pacientes de otros países?",
  "¿Qué hago si el paciente no se conecta a la sesión?",
  "¿Cuál es el único medio permitido para contactar pacientes?",
  "¿Qué hago ante una urgencia o situación de riesgo?",
];

const CHAT_ERROR_MESSAGE = "Ocurrió un error al procesar tu consulta. Por favor, intentá de nuevo en unos instantes.";

export function PsiChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    // El placeholder del asistente (vacío → muestra el spinner, ver render de
    // abajo) se agrega ACÁ, antes del fetch — no después de que resuelva.
    // Si no, mientras la conexión inicial tarda (más notorio con latencia de
    // red alta), no hay ningún indicador de carga del lado del asistente:
    // se ve el mensaje del usuario y nada más hasta que arranca a llegar
    // contenido, como si la app se hubiera quedado en blanco.
    setMessages((prev) => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: "" }]);
    setIsLoading(true);

    let assistantContent = "";

    try {
      const token = getValidToken();
      const resp = await fetch(PSI_CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-10),
        }),
      });

      if (!resp.ok) {
        throw new Error(CHAT_ERROR_MESSAGE);
      }

      if (!resp.body) {
        throw new Error(CHAT_ERROR_MESSAGE);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                return updated;
              });
            }
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }
    } catch (err) {
      // Actualiza el placeholder ya agregado (no suma un mensaje nuevo) — si
      // no, quedaría una burbuja vacía con el spinner girando para siempre
      // arriba del mensaje de error.
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "assistant",
          content: err instanceof Error ? err.message : CHAT_ERROR_MESSAGE,
        };
        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 z-[1250] h-14 w-14 rounded-full shadow-lg bg-[#C4E645] hover:bg-[#b3d43a] text-[#2d1a5e] flex items-center justify-center transition-colors"
          aria-label="Abrir asistente"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-[1249] w-[calc(100vw-2rem)] max-w-sm h-[28rem] flex flex-col rounded-2xl shadow-xl border-2 border-gray-200 bg-white overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-purple-50">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center">
                <Bot className="w-4 h-4 text-purple-700" />
              </div>
              <div>
                <p className="font-semibold text-sm text-gray-900">Asistente Psi Mammoliti</p>
                <p className="text-xs text-gray-500">Preguntame lo que necesites</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-lg hover:bg-purple-100 transition-colors"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 text-sm py-6 space-y-3">
                <Bot className="w-10 h-10 mx-auto text-purple-300" />
                <p className="font-medium text-gray-700">¡Hola! 👋</p>
                <p className="text-xs">Soy tu asistente de Psi Mammoliti. Preguntame sobre políticas, herramientas o cualquier duda.</p>
                <div className="space-y-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="block w-full text-left px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs text-gray-700 transition-colors"
                    >
                      💡 {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                      msg.role === "user"
                        ? "ml-auto bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    {msg.content ? (
                      msg.role === "assistant" ? (
                        <div className="prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-li:pl-1 prose-strong:text-gray-900 prose-headings:my-2">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <p>{processChildren(children)}</p>,
                              li: ({ children }) => <li>{processChildren(children)}</li>,
                              a: ({ href, children }) => (
                                <a
                                  href={href}
                                  className="text-purple-600 underline hover:text-purple-800 font-medium"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        msg.content
                      )
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-100">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribí tu pregunta..."
                disabled={isLoading}
                className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 disabled:opacity-50"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="p-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
